import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Container,
  Typography,
  Button,
  Icon,
  TextField,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { Link as RouterLink } from "react-router-dom";
import MaestroNavbar from "./blocks/MaestroNavbar.js";
import MaestroFooter from "./blocks/MaestroFooter.js";
import Seo from "./blocks/Seo.js";
import { getApiRoute, getPageRoute } from "src/services/Config.js";

const REPORT_URL = "/reports/Maestro-Growth-Full.pdf";

const ROLES = [
  { key: "teacher", label: "Teacher" },
  { key: "school-leader", label: "School leader" },
  { key: "district-partner", label: "District partner" },
];

const TOC = [
  {
    icon: "insights",
    text: "Cohort-level Conditional Growth Percentiles",
  },
  {
    icon: "psychology",
    text: "Per-teacher skill iteration timeline",
  },
  {
    icon: "layers",
    text: "The workflows that moved the needle",
  },
  {
    icon: "description",
    text: "Data appendix + reproducibility notes",
  },
  {
    icon: "groups",
    text: "Coach-teacher pairing outcomes",
  },
];

/**
 * Year-one report download landing page. Dark editorial layout: story
 * and table-of-contents on the left, gated form card on the right. On
 * submit, the form appends a row to the same Waitlist sheet the /waitlist
 * page uses (with role="report-download" appended to the notes column)
 * and triggers a direct PDF download.
 */
const ReportPage = () => {
  const theme = useTheme();

  const [form, setForm] = useState({
    role: "teacher",
    firstName: "",
    lastName: "",
    email: "",
    school: "",
    location: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [downloaded, setDownloaded] = useState(false);

  const setField = (key) => (event) => {
    setForm({ ...form, [key]: event.target.value });
  };

  const triggerDownload = () => {
    const link = document.createElement("a");
    link.href = REPORT_URL;
    link.download = "Maestro-Growth-Full.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSubmitting(true);
    try {
      await axios.post(
        getApiRoute("waitlist", "SUBMIT"),
        {
          ...form,
          notes: form.notes
            ? `[Report download] ${form.notes}`
            : "[Report download]",
        },
        { withCredentials: true }
      );
      triggerDownload();
      setDownloaded(true);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        "We couldn't submit your download. Please try again shortly.";
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Shared input styling. Matches WaitlistPage.js so the "get the report" form
   * feels like the same product surface as the "join the waitlist" form.
   */
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: "#fafafa",
      borderRadius: 1.5,
      fontSize: "0.9375rem",
      "& fieldset": { borderColor: theme.palette.text.tertiary },
      "&:hover fieldset": { borderColor: theme.palette.text.secondary },
      "&.Mui-focused fieldset": { borderColor: theme.palette.success.main },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.success.main,
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#000000",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Seo
        title="Year-one report · Maestro"
        description="Download Maestro's Year One report: the full methodology behind the 200-400% growth-vs-norm result across 28 classrooms."
        type="article"
      />

      <MaestroNavbar variant="dark" />

      {/* Dark editorial hero + form card — fills viewport height between nav and footer */}
      <Box
        component="section"
        sx={{
          background:
            "linear-gradient(180deg, #000000 0%, #0f172a 45%, #000000 100%)",
          color: theme.palette.paper.primary,
          position: "relative",
          overflow: "hidden",
          py: { xs: 8, md: 12 },
          flex: 1,
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* Ambient glows */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "-120px",
            right: "-120px",
            width: 480,
            height: 480,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(34,197,94,0.18) 0%, transparent 60%)",
            pointerEvents: "none",
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            bottom: "-140px",
            left: "-100px",
            width: 460,
            height: 460,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 60%)",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.1fr 1fr" },
              gap: { xs: 6, md: 10 },
              alignItems: "stretch",
            }}
          >
            {/* LEFT — story + TOC */}
            <Box>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  color: "#4ade80",
                  fontWeight: 700,
                  px: 1.5,
                  py: 0.75,
                  bgcolor: "rgba(34,197,94,0.12)",
                  border: "1px solid rgba(34,197,94,0.3)",
                  borderRadius: 999,
                  mb: 3,
                }}
              >
                Year 1 report · 2025-26
              </Box>

              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: "2.5rem", md: "3.75rem" },
                  fontWeight: 800,
                  letterSpacing: "-0.035em",
                  lineHeight: 1.05,
                  color: theme.palette.paper.primary,
                  mb: 3,
                }}
              >
                What we learned across{" "}
                <Box
                  component="span"
                  sx={{
                    background:
                      "linear-gradient(90deg, #22c55e, #4ade80)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "transparent",
                  }}
                >
                  2000+ students
                </Box>
                .
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: "1.0625rem", md: "1.1875rem" },
                  color: "rgba(255,255,255,0.75)",
                  lineHeight: 1.55,
                  mb: 4,
                  maxWidth: 520,
                }}
              >
                The full methodology behind the 200-400% growth-vs-norm result.
                Teacher-by-teacher, cohort-by-cohort, with the data appendix.
              </Typography>

              {/* Table of contents */}
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 3,
                  p: { xs: 2.5, md: 3 },
                  maxWidth: 520,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "rgba(74,222,128,0.9)",
                    fontWeight: 700,
                    mb: 2,
                  }}
                >
                  What's inside
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  {TOC.map((item) => (
                    <Box
                      key={item.text}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Icon
                        baseClassName="material-symbols-outlined"
                        sx={{
                          fontSize: 20,
                          color: "#4ade80",
                          flexShrink: 0,
                        }}
                      >
                        {item.icon}
                      </Icon>
                      <Typography
                        sx={{
                          fontSize: "0.9375rem",
                          color: "rgba(255,255,255,0.85)",
                          lineHeight: 1.4,
                        }}
                      >
                        {item.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* RIGHT — form card. Stretches to match left column; content centered vertically inside */}
            <Box
              sx={{
                bgcolor: theme.palette.paper.primary,
                color: theme.palette.text.primary,
                borderRadius: 4,
                p: { xs: 3.5, md: 5 },
                boxShadow:
                  "0 40px 80px -20px rgba(0,0,0,0.5), 0 12px 24px rgba(0,0,0,0.15)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              {downloaded ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      bgcolor: theme.palette.success.main,
                      color: theme.palette.paper.primary,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2.5,
                    }}
                  >
                    <Icon
                      baseClassName="material-symbols-outlined"
                      sx={{ fontSize: 32, color: theme.palette.paper.primary }}
                    >
                      check
                    </Icon>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 800,
                      letterSpacing: "-0.02em",
                      color: theme.palette.text.primary,
                      mb: 1,
                    }}
                  >
                    Your download has started.
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.9375rem",
                      color: theme.palette.text.secondary,
                      mb: 3,
                    }}
                  >
                    Check your Downloads folder. If nothing happened, click
                    below to retry.
                  </Typography>
                  <Button
                    onClick={triggerDownload}
                    sx={{
                      bgcolor: theme.palette.success.main,
                      color: theme.palette.paper.primary,
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 999,
                      px: 3,
                      py: 1.25,
                      "&:hover": { bgcolor: "#15803d" },
                    }}
                  >
                    <Icon
                      baseClassName="material-symbols-outlined"
                      sx={{ fontSize: 20, mr: 0.75 }}
                    >
                      download
                    </Icon>
                    Download again
                  </Button>
                  <Box sx={{ mt: 3 }}>
                    <Button
                      component={RouterLink}
                      to={getPageRoute("home", "HOME")}
                      sx={{
                        color: theme.palette.text.secondary,
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      Back to homepage
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box component="form" onSubmit={handleSubmit}>
                  <Typography
                    sx={{
                      fontSize: { xs: "1.5rem", md: "1.75rem" },
                      fontWeight: 800,
                      letterSpacing: "-0.02em",
                      color: theme.palette.text.primary,
                      mb: 0.75,
                    }}
                  >
                    Get the report
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.9375rem",
                      color: theme.palette.text.secondary,
                      mb: { xs: 4, md: 4 },
                    }}
                  >
                    32-page PDF. We don't share your info.
                  </Typography>

                  {/* Role picker — stacks on mobile so tap targets stay full-width */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 1.25,
                      mb: { xs: 3.5, md: 3 },
                      flexWrap: "wrap",
                    }}
                  >
                    {ROLES.map((r) => (
                      <Box
                        key={r.key}
                        onClick={() => setForm({ ...form, role: r.key })}
                        sx={{
                          flex: 1,
                          minWidth: 100,
                          textAlign: "center",
                          px: 1.5,
                          py: { xs: 1.5, sm: 1.25 },
                          border: `1px solid ${
                            form.role === r.key
                              ? theme.palette.success.main
                              : theme.palette.text.tertiary
                          }`,
                          borderRadius: 2,
                          bgcolor:
                            form.role === r.key ? "#f0fdf4" : "transparent",
                          color:
                            form.role === r.key
                              ? "#14532d"
                              : theme.palette.text.secondary,
                          fontSize: "0.9375rem",
                          fontWeight: 700,
                          cursor: "pointer",
                          transition:
                            "border-color 0.15s, background 0.15s, color 0.15s",
                        }}
                      >
                        {r.label}
                      </Box>
                    ))}
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <TextField
                      label="First name"
                      required
                      value={form.firstName}
                      onChange={setField("firstName")}
                      fullWidth
                      sx={inputSx}
                    />
                    <TextField
                      label="Last name"
                      required
                      value={form.lastName}
                      onChange={setField("lastName")}
                      fullWidth
                      sx={inputSx}
                    />
                  </Box>
                  <TextField
                    label="School email"
                    type="email"
                    required
                    value={form.email}
                    onChange={setField("email")}
                    fullWidth
                    sx={{ ...inputSx, mb: 2 }}
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <TextField
                      label="School / district"
                      required
                      value={form.school}
                      onChange={setField("school")}
                      fullWidth
                      sx={inputSx}
                    />
                    <TextField
                      label="City, State"
                      value={form.location}
                      onChange={setField("location")}
                      fullWidth
                      sx={inputSx}
                    />
                  </Box>

                  {errorMessage && (
                    <Box
                      sx={{
                        mt: 1,
                        mb: 2,
                        p: 1.5,
                        border: `1px solid ${theme.palette.error.main}`,
                        borderRadius: 2,
                        bgcolor: "#fef2f2",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          color: theme.palette.error.main,
                        }}
                      >
                        {errorMessage}
                      </Typography>
                    </Box>
                  )}

                  <Button
                    type="submit"
                    disabled={submitting}
                    fullWidth
                    sx={{
                      mt: { xs: 2.5, md: 2 },
                      bgcolor: theme.palette.success.main,
                      color: theme.palette.paper.primary,
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 999,
                      py: { xs: 2, md: 1.75 },
                      fontSize: "1rem",
                      "&:hover": {
                        bgcolor: "#15803d",
                        transform: "translateY(-1px)",
                      },
                      "&.Mui-disabled": {
                        bgcolor: theme.palette.text.tertiary,
                        color: theme.palette.paper.primary,
                      },
                      transition: "transform 0.15s, background 0.15s",
                    }}
                  >
                    {submitting ? (
                      "Submitting..."
                    ) : (
                      <>
                        <Icon
                          baseClassName="material-symbols-outlined"
                          sx={{ fontSize: 20, mr: 0.75 }}
                        >
                          download
                        </Icon>
                        Download the report
                      </>
                    )}
                  </Button>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: theme.palette.text.quaternary,
                      textAlign: "center",
                      mt: 1.5,
                    }}
                  >
                    32-page PDF · 1.5MB
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      <MaestroFooter variant="dark" />
    </Box>
  );
};

export default ReportPage;
